import { useLocation } from "react-router-dom"
import {
  DEFAULT_OG_IMAGE,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  SITE,
  absoluteUrl,
  canonicalUrl,
  ogImage,
} from "./site"

/* ----------------------------------------------------------------------------
   <Seo> — per-route document metadata.

   React 19 hoists <title>, <meta> and <link> rendered anywhere in the tree
   into <head>, on the client *and* during server rendering. That gives us one
   declaration site for both: during SPA navigation React swaps the tags and
   reverts them on unmount; during prerendering the build script lifts the
   hoisted tags out of the stream and into the static <head>.

   JSON-LD is deliberately NOT hoisted — React leaves <script type="application/
   ld+json"> inline, which is valid and fully supported by search engines.
---------------------------------------------------------------------------- */

export type SeoProps = {
  /** Page title WITHOUT the brand suffix — it is appended automatically. */
  title: string
  description: string
  /** Path or absolute URL of the share image. Falls back to the site default. */
  image?: string
  /** "website" for pages, "article" for blog posts and case studies. */
  type?: "website" | "article"
  /** Set true for pages that must never be indexed (admin, thin utility pages). */
  noindex?: boolean
  /** Override the canonical path. Defaults to the current location. */
  canonicalPath?: string
  /** Structured data objects, emitted as one <script> each. */
  jsonLd?: object[]
  /** Article-only metadata. */
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
    section?: string
    tags?: string[]
  }
}

export function Seo({
  title,
  description,
  image,
  type = "website",
  noindex = false,
  canonicalPath,
  jsonLd = [],
  article,
}: SeoProps) {
  const { pathname } = useLocation()
  const canonical = canonicalUrl(canonicalPath ?? pathname)
  const fullTitle =
    title === SITE.name ? title : `${title} | ${SITE.titleSuffix}`
  const share = image ? ogImage(image) : { url: DEFAULT_OG_IMAGE, sized: true }

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={SITE.locale} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={share.url} />
      {share.sized ? (
        <>
          <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />
          <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />
        </>
      ) : null}
      <meta property="og:image:alt" content={title} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={share.url} />
      {SITE.twitterHandle ? (
        <meta name="twitter:site" content={SITE.twitterHandle} />
      ) : null}

      {/* Article metadata */}
      {article?.publishedTime ? (
        <meta property="article:published_time" content={article.publishedTime} />
      ) : null}
      {article?.modifiedTime ? (
        <meta property="article:modified_time" content={article.modifiedTime} />
      ) : null}
      {article?.author ? (
        <meta property="article:author" content={article.author} />
      ) : null}
      {article?.section ? (
        <meta property="article:section" content={article.section} />
      ) : null}
      {article?.tags?.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {jsonLd.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
        />
      ))}
    </>
  )
}

/**
 * Serialises structured data for inline embedding. `<` and `&` are escaped so a
 * stray "</script>" inside user-authored CMS copy can never break out of the
 * script element.
 */
function safeJsonLd(schema: object): string {
  return JSON.stringify(schema)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
}

/* ----------------------------------------------------------------------------
   Structured data builders
---------------------------------------------------------------------------- */

const ORG_ID = `${canonicalUrl("/")}#organization`
const SITE_ID = `${canonicalUrl("/")}#website`

/** Organization — the entity every other schema points back at. */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    /*
      Organization, not ProfessionalService. ProfessionalService is a
      LocalBusiness subtype and Google expects a verifiable postal address on
      it — Sableboxx does not publish one, so claiming the type without the
      address would be a structured-data error rather than a missing field.
    */
    "@type": "Organization",
    "@id": ORG_ID,
    name: SITE.name,
    legalName: SITE.legalName,
    description: SITE.description,
    url: canonicalUrl("/"),
    logo: {
      "@type": "ImageObject",
      url: absoluteUrl("/brand/logo.png"),
    },
    image: DEFAULT_OG_IMAGE,
    email: SITE.email,
    telephone: SITE.phone,
    foundingDate: SITE.foundingYear,
    /* Locality only — no PostalAddress. See the note in site.ts. */
    location: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: SITE.address.city,
        addressCountry: SITE.address.country,
      },
    },
    areaServed: [
      { "@type": "Country", name: SITE.address.countryName },
      { "@type": "Place", name: "Worldwide" },
    ],
    knowsAbout: [
      "Brand Strategy",
      "Visual Identity",
      "Web Design",
      "Web Development",
      "Digital Product Design",
      "Search Engine Optimisation",
    ],
    ...(SITE.social.length > 0 ? { sameAs: SITE.social } : {}),
  }
}

/** WebSite — enables sitelinks and names the publisher. */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": SITE_ID,
    name: SITE.name,
    url: canonicalUrl("/"),
    description: SITE.description,
    inLanguage: SITE.lang,
    publisher: { "@id": ORG_ID },
  }
}

/** Breadcrumbs — `trail` excludes Home, which is prepended automatically. */
export function breadcrumbSchema(trail: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [{ name: "Home", path: "/" }, ...trail].map(
      (crumb, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: crumb.name,
        item: canonicalUrl(crumb.path),
      }),
    ),
  }
}

/** Article — blog posts. */
export function articleSchema(post: {
  title: string
  slug: string
  excerpt: string
  cover: string
  author: string
  date: string
  category: string
}) {
  const url = canonicalUrl(`/insights/${post.slug}`)
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: post.title,
    description: post.excerpt,
    image: absoluteUrl(post.cover),
    datePublished: post.date,
    dateModified: post.date,
    articleSection: post.category,
    inLanguage: SITE.lang,
    author: { "@type": "Person", name: post.author },
    publisher: { "@id": ORG_ID },
    isPartOf: { "@id": SITE_ID },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  }
}

/** CreativeWork — case studies. */
export function caseStudySchema(work: {
  title: string
  slug: string
  client: string
  summary: string
  cover: string
  year: string
  updated: string
  services: string[]
}) {
  const url = canonicalUrl(`/work/${work.slug}`)
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": `${url}#case-study`,
    name: work.title,
    headline: work.title,
    description: work.summary,
    image: absoluteUrl(work.cover),
    dateCreated: work.year,
    dateModified: work.updated,
    inLanguage: SITE.lang,
    creator: { "@id": ORG_ID },
    about: work.client,
    keywords: work.services.join(", "),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  }
}

/** Service — one per offering, linked to the studio. */
export function serviceSchema(service: {
  t: string
  slug: string
  d: string
  items: readonly string[]
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.t,
    description: service.d,
    provider: { "@id": ORG_ID },
    serviceType: service.t,
    areaServed: { "@type": "Country", name: SITE.address.countryName },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: service.t,
      itemListElement: service.items.map((item) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: item },
      })),
    },
  }
}

/** FAQPage — eligible for FAQ rich results. */
export function faqSchema(faqs: readonly { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }
}

/** ItemList — collection pages (work index, insights index). */
export function itemListSchema(
  items: { name: string; path: string }[],
  listName: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      url: canonicalUrl(item.path),
    })),
  }
}
